import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import type { VoiceCall } from '../types/voiceCall';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
  return response.json();
}

/** Wire shape → app shape. The backend speaks snake_case. */
function toVoiceCall(raw: any): VoiceCall {
  return {
    callId: raw.call_id,
    threadId: raw.thread_id ?? null,
    status: raw.status,
    isFinal: Boolean(raw.is_final),
    toNumber: raw.to_number,
    carrierName: raw.carrier_name ?? null,
    policyNumber: raw.policy_number ?? null,
    objective: raw.objective ?? '',
    verificationFacts: raw.verification_facts ?? null,
    transcript: Array.isArray(raw.transcript) ? raw.transcript : [],
    extracted: raw.extracted && typeof raw.extracted === 'object' ? raw.extracted : {},
    summary: raw.summary ?? null,
    error: raw.error ?? null,
    durationSecs: raw.duration_secs ?? null,
    createdAt: raw.created_at,
    endedAt: raw.ended_at ?? null,
  };
}

/**
 * The newest call on a thread, or null if there isn't one.
 *
 * This is how the modal finds a call the copilot tool has started but not
 * yet returned from: at that moment the browser knows the thread and
 * nothing else, because the tool's result — including the call id — only
 * reaches the stream once the call is over.
 */
export async function fetchLatestCallForThread(threadId: string): Promise<VoiceCall | null> {
  const url = buildApiUrl(
    `${requireTenantPrefix()}/voice-calls/latest?thread_id=${encodeURIComponent(threadId)}`,
  );
  const res = await fetch(url, { headers: getAuthHeaders() });
  const body = await readJson<any>(res, 'Could not load the call');
  return body ? toVoiceCall(body) : null;
}

export async function fetchCall(callId: string): Promise<VoiceCall> {
  const url = buildApiUrl(`${requireTenantPrefix()}/voice-calls/${encodeURIComponent(callId)}`);
  const res = await fetch(url, { headers: getAuthHeaders() });
  return toVoiceCall(await readJson<any>(res, 'Could not load the call'));
}

/** Wire shape → app shape for the chat-card receipt. */
export function toVoiceCallSummary(raw: any): import('../types/voiceCall').VoiceCallSummary {
  return {
    callId: raw.call_id,
    carrierName: raw.carrier_name || '',
    phoneNumber: raw.phone_number || '',
    policyNumber: raw.policy_number || undefined,
    objective: raw.objective || '',
    status: raw.status || 'completed',
    durationSecs: raw.duration_secs ?? null,
    turnCount: raw.turn_count ?? 0,
    extracted: raw.extracted && typeof raw.extracted === 'object' ? raw.extracted : {},
  };
}
